import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./i18n"; // bootstraps i18next before any component renders
import { useTranslation } from "react-i18next";
// App.jsx (legacy mockup) is no longer routed — see the /* route below. The files stay in the
// repo; nothing imports them.
import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth, RequirePermission, RequireAnyRole } from "./auth/RequireAuth";
import { AppShell } from "./components/AppShell";
import { ToastProvider } from "./components/Toast";
import { ConfirmProvider } from "./components/ConfirmDialog";
import DashboardPage from "./pages/DashboardPage";
const AdminCompanyDetailPage = React.lazy(() => import("./pages/admin/AdminCompanyDetailPage"));
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ForbiddenPage from "./pages/ForbiddenPage";
import NotFoundPage from "./pages/NotFoundPage";
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));
const AdminCompaniesPage = React.lazy(() => import("./pages/admin/AdminCompaniesPage"));
const AdminUsersPage = React.lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminInvitationsPage = React.lazy(() => import("./pages/admin/AdminInvitationsPage"));
const AdminCategoriesPage = React.lazy(() => import("./pages/admin/catalog/AdminCategoriesPage"));
const AdminProductsPage = React.lazy(() => import("./pages/admin/catalog/AdminProductsPage"));
const AdminProductEditorPage = React.lazy(() => import("./pages/admin/catalog/AdminProductEditorPage"));
const AdminSparePartsPage = React.lazy(() => import("./pages/admin/catalog/AdminSparePartsPage"));
const AdminSparePartEditorPage = React.lazy(() => import("./pages/admin/catalog/AdminSparePartEditorPage"));
const AdminComparisonPage = React.lazy(() => import("./pages/admin/catalog/AdminComparisonPage"));
const CatalogIndexPage = React.lazy(() => import("./pages/catalog/CatalogIndexPage"));
const CatalogCategoryPage = React.lazy(() => import("./pages/catalog/CatalogCategoryPage"));
const CatalogProductPage = React.lazy(() => import("./pages/catalog/CatalogProductPage"));
// IMP-10: customer-facing model comparison disabled for now (admin kept). Restore by uncommenting this + the /comparison route + the AppShell nav entry.
// import ComparisonPage from "./pages/catalog/ComparisonPage";
const SparePartsPage = React.lazy(() => import("./pages/catalog/SparePartsPage"));
const MaterialsPage = React.lazy(() => import("./pages/materials/MaterialsPage"));
const AdminMaterialsPage = React.lazy(() => import("./pages/admin/materials/AdminMaterialsPage"));
const PumpSelectorPage = React.lazy(() => import("./pages/PumpSelectorPage"));
const NewsListPage = React.lazy(() => import("./pages/news/NewsListPage"));
const NewsDetailPage = React.lazy(() => import("./pages/news/NewsDetailPage"));
const AdminNewsPage = React.lazy(() => import("./pages/admin/news/AdminNewsPage"));
const AdminNewsEditorPage = React.lazy(() => import("./pages/admin/news/AdminNewsEditorPage"));
const FaqPage = React.lazy(() =>
  import("./pages/service/ServicePanelPage").then((m) => ({ default: m.FaqPage })),
);
// Lazy-loaded: pulls in three.js (~600 kB) only on the exploded-view route,
// keeping it out of the main bundle for every other page.
const ExplodedViewPage = React.lazy(() =>
  import("./pages/service/ExplodedViewPage").then((m) => ({ default: m.ExplodedViewPage })),
);
const AdminFaqPage = React.lazy(() => import("./pages/admin/service/AdminFaqPage"));
const AdminTutorialsPage = React.lazy(() => import("./pages/admin/service/AdminTutorialsPage"));
const ContractsPage = React.lazy(() => import("./pages/ContractsPage"));
const AdminContractsPage = React.lazy(() => import("./pages/admin/AdminContractsPage"));
const OrdersPage = React.lazy(() => import("./pages/OrdersPage"));
const OrderEditorPage = React.lazy(() => import("./pages/OrderEditorPage"));
const OffersPage = React.lazy(() => import("./pages/OffersPage"));
const OfferEditorPage = React.lazy(() => import("./pages/OfferEditorPage"));
const LeadsPage = React.lazy(() => import("./pages/leads/LeadsPage"));
const AgentsPage = React.lazy(() => import("./pages/agents/AgentsPage"));
const EtlPage = React.lazy(() => import("./pages/admin/EtlPage"));
const AgentDetailPage = React.lazy(() => import("./pages/agents/AgentDetailPage"));
const LeadDetailPage = React.lazy(() => import("./pages/leads/LeadDetailPage"));
const MyProjectsPage = React.lazy(() => import("./pages/MyProjectsPage"));
const ProjectEditorPage = React.lazy(() => import("./pages/ProjectEditorPage"));
const CustomerProjectsPage = React.lazy(() => import("./pages/admin/CustomerProjectsPage"));
const CustomerProjectReviewPage = React.lazy(() => import("./pages/admin/CustomerProjectReviewPage"));
const AdminOrdersPage = React.lazy(() => import("./pages/admin/AdminOrdersPage"));
const ForecastsPage = React.lazy(() => import("./pages/ForecastsPage"));
const FinancePage = React.lazy(() => import("./pages/finance/FinancePage"));
const FinanceCompanyPage = React.lazy(() => import("./pages/finance/FinanceCompanyPage"));
const AdminErpOutboxPage = React.lazy(() => import("./pages/admin/AdminErpOutboxPage"));
const AdminAuditLogPage = React.lazy(() => import("./pages/admin/AdminAuditLogPage"));
const AdminEmailPreviewPage = React.lazy(() => import("./pages/admin/AdminEmailPreviewPage"));
const SupportListPage = React.lazy(() => import("./pages/support/SupportListPage"));
const SupportSubmitPage = React.lazy(() => import("./pages/support/SupportSubmitPage"));
const SupportDetailPage = React.lazy(() => import("./pages/support/SupportDetailPage"));
const TmTasksPage = React.lazy(() => import("./pages/tm/TmTasksPage"));
const TmTaskNewPage = React.lazy(() => import("./pages/tm/TmTaskNewPage"));
const TmTaskDetailPage = React.lazy(() => import("./pages/tm/TmTaskDetailPage"));
const TmProjectsPage = React.lazy(() => import("./pages/tm/TmProjectsPage"));
const TmProjectNewPage = React.lazy(() => import("./pages/tm/TmProjectNewPage"));
const TmProjectDetailPage = React.lazy(() => import("./pages/tm/TmProjectDetailPage"));
const TmMeetingsPage = React.lazy(() => import("./pages/tm/TmMeetingsPage"));
const TmMeetingNewPage = React.lazy(() => import("./pages/tm/TmMeetingNewPage"));
const TmMeetingDetailPage = React.lazy(() => import("./pages/tm/TmMeetingDetailPage"));
const TmTeamsPage = React.lazy(() => import("./pages/tm/TmTeamsPage"));
const TmActivityPage = React.lazy(() => import("./pages/tm/TmActivityPage"));
const TmOverviewPage = React.lazy(() => import("./pages/tm/TmOverviewPage"));
const ToolsLandingPage = React.lazy(() => import("./pages/tools/ToolsLandingPage"));
const InnovationsPage = React.lazy(() => import("./pages/tools/InnovationsPage"));
const NewInnovationPage = React.lazy(() => import("./pages/tools/NewInnovationPage"));
const InnovationDetailPage = React.lazy(() => import("./pages/tools/InnovationDetailPage"));
const AdminMarketsPage = React.lazy(() => import("./pages/admin/AdminMarketsPage"));
const AdminKpiPage = React.lazy(() => import("./pages/admin/AdminKpiPage"));
const AdminSettingsPage = React.lazy(() => import("./pages/admin/AdminSettingsPage"));
const MarketsOverviewPage = React.lazy(() => import("./pages/MarketsOverviewPage"));
const AdminSupportInboxPage = React.lazy(() => import("./pages/admin/support/AdminSupportInboxPage"));
const AdminSupportDetailPage = React.lazy(() => import("./pages/admin/support/AdminSupportDetailPage"));
const OrderImportReviewPage = React.lazy(() => import("./pages/orders/OrderImportReviewPage"));
const MailSettingsPage = React.lazy(() => import("./pages/MailSettingsPage"));
import "./styles.css";

// Shown while a lazy route chunk downloads. Uses the shared loading copy so
// every page transition reads the same as in-page loading states.
function PageFallback() {
  const { t } = useTranslation();
  return (
    <div style={{ padding: 24, color: "var(--ink-3)", fontSize: 14 }}>
      {t("common.loading", "Loading\u2026")}
    </div>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("missing #root");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
        <ConfirmProvider>
        <React.Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public auth routes (no shell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />

          {/* Authenticated routes inside the new AppShell */}
          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route
              path="/contact"
              element={
                <RequireAnyRole roles={["CUST_MANAGER", "CUST_PROCUREMENT", "CUST_SERVICE"]}>
                  <ContactPage />
                </RequireAnyRole>
              }
            />
            <Route
              path="/admin/companies"
              element={
                <RequirePermission permission="companies:view">
                  <AdminCompaniesPage />
                </RequirePermission>
              }
            />
            <Route
              // markets:view, not markets:manage. The sales director holds view, not manage, so
              // until now this page was not merely thin for them — it did not exist, in the menu or
              // on the route. The Sales people tab is written for exactly that reader. Editing a
              // market is still gated on markets:manage inside the page.
              path="/admin/markets"
              element={
                <RequirePermission permission="markets:view">
                  <AdminMarketsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/kpi"
              element={
                <RequirePermission anyOf={["kpi:view_team", "kpi:view_all"]}>
                  <AdminKpiPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <RequirePermission permission="settings:manage">
                  <AdminSettingsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/markets"
              element={
                <RequirePermission permission="markets:view">
                  <MarketsOverviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/companies/:id"
              element={
                <RequirePermission permission="companies:view">
                  <AdminCompanyDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequirePermission permission="users:view">
                  <AdminUsersPage />
                </RequirePermission>
              }
            />
            <Route path="/admin/invitations" element={<AdminInvitationsPage />} />
            <Route
              path="/admin/catalog/categories"
              element={
                <RequirePermission permission="catalog:edit">
                  <AdminCategoriesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/products"
              element={
                <RequirePermission permission="catalog:edit">
                  <AdminProductsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/products/:id"
              element={
                <RequirePermission permission="catalog:edit">
                  <AdminProductEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/spare-parts"
              element={
                <RequirePermission permission="catalog:edit">
                  <AdminSparePartsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/spare-parts/:id"
              element={
                <RequirePermission permission="catalog:edit">
                  <AdminSparePartEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/catalog/comparison"
              element={
                <RequirePermission permission="catalog:edit">
                  <AdminComparisonPage />
                </RequirePermission>
              }
            />

            {/* Customer catalog browse */}
            <Route path="/catalog" element={<CatalogIndexPage />} />
            <Route path="/spare-parts" element={<SparePartsPage />} />
            {/* IMP-10: customer-facing model comparison disabled for now (admin kept).
            <Route path="/comparison" element={<ComparisonPage />} /> */}
            <Route path="/catalog/:slug" element={<CatalogCategoryPage />} />
            <Route path="/catalog/:categorySlug/:sku" element={<CatalogProductPage />} />

            {/* Materials */}
            <Route path="/materials" element={<MaterialsPage />} />
            <Route
              path="/admin/materials"
              element={
                <RequirePermission permission="materials:upload">
                  <AdminMaterialsPage />
                </RequirePermission>
              }
            />

            {/* Pump Selector */}
            <Route path="/pump-selector" element={<PumpSelectorPage />} />

            {/* News */}
            <Route path="/news" element={<NewsListPage />} />
            <Route path="/news/:slug" element={<NewsDetailPage />} />
            <Route
              path="/admin/news"
              element={
                <RequirePermission permission="news:create">
                  <AdminNewsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/news/:id"
              element={
                <RequirePermission permission="news:create">
                  <AdminNewsEditorPage />
                </RequirePermission>
              }
            />

            {/* FAQ + Tutorials (Faza 2) — split into two top-level routes */}
            <Route
              path="/faq"
              element={
                <RequirePermission permission="service:view">
                  <FaqPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tutorials"
              element={
                <RequirePermission permission="service:view">
                    <ExplodedViewPage />
                  
                </RequirePermission>
              }
            />
            {/* Old /service URL kept as redirect to FAQ for any bookmarks. */}
            <Route path="/service" element={<Navigate to="/faq" replace />} />
            <Route
              path="/admin/service/faq"
              element={
                <RequirePermission permission="service:create">
                  <AdminFaqPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/service/tutorials"
              element={
                <RequirePermission permission="service:create">
                  <AdminTutorialsPage />
                </RequirePermission>
              }
            />

            {/* Contracts (Faza 3) */}
            <Route
              path="/contracts"
              element={
                <RequirePermission permission="contracts:view">
                  <ContractsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/contracts"
              element={
                <RequirePermission permission="contracts:create">
                  <AdminContractsPage />
                </RequirePermission>
              }
            />

            {/* Orders (Faza 4a) */}
            <Route
              path="/orders"
              element={
                <RequirePermission permission="orders:view">
                  <OrdersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/orders/:id"
              element={
                <RequirePermission permission="orders:view">
                  <OrderEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/orders/imports/:id"
              element={
                <RequirePermission permission="orders:import">
                  <OrderImportReviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <RequirePermission permission="orders:transition">
                  <AdminOrdersPage />
                </RequirePermission>
              }
            />

            {/* Customer Projects (investment → quote) */}
            <Route
              path="/my-projects"
              element={
                <RequirePermission permission="customer_projects:edit">
                  <MyProjectsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/my-projects/:id"
              element={
                <RequirePermission permission="customer_projects:view">
                  <ProjectEditorPage />
                </RequirePermission>
              }
            />

            {/* Customer Projects — sales/director (admin surface) */}
            <Route
              path="/admin/customer-projects"
              element={
                <RequirePermission permission="customer_projects:price">
                  <CustomerProjectsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/customer-projects/:id"
              element={
                <RequirePermission permission="customer_projects:price">
                  <CustomerProjectReviewPage />
                </RequirePermission>
              }
            />

            {/* Offers (quotes) */}
            <Route
              path="/offers"
              element={
                <RequirePermission permission="offers:view">
                  <OffersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/offers/:id"
              element={
                <RequirePermission permission="offers:view">
                  <OfferEditorPage />
                </RequirePermission>
              }
            />

            {/* ETL — the import pipeline's control screen (superadmin/admin) */}
            <Route
              path="/admin/etl"
              element={
                <RequirePermission permission="etl:view">
                  <EtlPage />
                </RequirePermission>
              }
            />

            {/* Agents (internal sales-agent directory) */}
            <Route
              path="/agents"
              element={
                <RequirePermission permission="agents:view">
                  <AgentsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/agents/:id"
              element={
                <RequirePermission permission="agents:view">
                  <AgentDetailPage />
                </RequirePermission>
              }
            />

            {/* Leads (pre-customer sales pipeline) */}
            <Route
              path="/leads"
              element={
                <RequirePermission permission="leads:view">
                  <LeadsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/leads/:id"
              element={
                <RequirePermission permission="leads:view">
                  <LeadDetailPage />
                </RequirePermission>
              }
            />

            {/* Forecasts (Faza 4a) */}
            <Route
              path="/forecasts"
              element={
                <RequirePermission permission="forecasts:view">
                  <ForecastsPage />
                </RequirePermission>
              }
            />

            {/* Finance */}
            <Route
              path="/finance"
              element={
                <RequirePermission anyOf={["finance:view_own", "finance:view_assigned", "finance:view_all"]}>
                  <FinancePage />
                </RequirePermission>
              }
            />
            <Route
              path="/finance/companies/:id"
              element={
                <RequirePermission anyOf={["finance:view_assigned", "finance:view_all"]}>
                  <FinanceCompanyPage />
                </RequirePermission>
              }
            />

            {/* Support (tickets + claims) */}
            <Route
              path="/support"
              element={
                <RequirePermission permission="support:view_own">
                  <SupportListPage />
                </RequirePermission>
              }
            />
            <Route
              path="/support/new"
              element={
                <RequirePermission permission="support:submit">
                  <SupportSubmitPage />
                </RequirePermission>
              }
            />
            <Route
              path="/support/:id"
              element={
                <RequirePermission permission="support:view_own">
                  <SupportDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/support"
              element={
                <RequirePermission anyOf={["support:view_all", "support:view_assigned"]}>
                  <AdminSupportInboxPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/support/:id"
              element={
                <RequirePermission anyOf={["support:view_all", "support:view_assigned"]}>
                  <AdminSupportDetailPage />
                </RequirePermission>
              }
            />

            {/* Tools — IMP-internal launcher with module tiles. */}
            <Route
              path="/tools"
              element={
                <RequirePermission permission="tm:access">
                  <ToolsLandingPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tools/innovations"
              element={
                <RequirePermission permission="innovation:submit">
                  <InnovationsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tools/innovations/new"
              element={
                <RequirePermission permission="innovation:submit">
                  <NewInnovationPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tools/innovations/:id"
              element={
                <RequirePermission permission="innovation:submit">
                  <InnovationDetailPage />
                </RequirePermission>
              }
            />

            {/* IMP TM (Phase 1a — tasks only; projects/meetings/team in 1b) */}
            <Route
              path="/tm"
              element={
                <RequirePermission permission="tm:access">
                  <TmOverviewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/tasks"
              element={
                <RequirePermission permission="tm:access">
                  <TmTasksPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/tasks/new"
              element={
                <RequirePermission permission="tm:access">
                  <TmTaskNewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/tasks/:id"
              element={
                <RequirePermission permission="tm:access">
                  <TmTaskDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/projects"
              element={
                <RequirePermission permission="tm:access">
                  <TmProjectsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/projects/new"
              element={
                <RequirePermission anyOf={["tm:manage_team"]}>
                  <TmProjectNewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/projects/:id"
              element={
                <RequirePermission permission="tm:access">
                  <TmProjectDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/meetings"
              element={
                <RequirePermission permission="tm:access">
                  <TmMeetingsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/meetings/new"
              element={
                <RequirePermission permission="tm:access">
                  <TmMeetingNewPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/meetings/:id"
              element={
                <RequirePermission permission="tm:access">
                  <TmMeetingDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/team"
              element={
                <RequirePermission anyOf={["tm:manage_team"]}>
                  <TmTeamsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tm/activity"
              element={
                <RequirePermission permission="tm:access">
                  <TmActivityPage />
                </RequirePermission>
              }
            />

            {/* Settings → "Moj mejl": the caller's own mailbox (2026-07-30 mail spec).
                mail:connect is the same role set that may send from the portal. */}
            <Route
              path="/settings/mail"
              element={
                <RequirePermission permission="mail:connect">
                  <MailSettingsPage />
                </RequirePermission>
              }
            />

            {/* ERP outbox (Faza 4b ops) — SUPERADMIN/ADMIN only via @PreAuthorize on BE */}
            <Route path="/admin/erp" element={<AdminErpOutboxPage />} />
            <Route
              path="/admin/audit-log"
              element={
                <RequirePermission permission="audit:view">
                  <AdminAuditLogPage />
                </RequirePermission>
              }
            />
            <Route path="/admin/emails/preview" element={<AdminEmailPreviewPage />} />
          </Route>

          {/* An unmatched URL shows 404 — STORY-0.10.6, implemented 2026-10-08.
              Until now this slot mounted the legacy mockup (App.jsx) behind RequireAuth alone, so
              any path that matched nothing rendered fabricated KPIs, orders and pumps from
              components/data.jsx: outside AppShell, no navigation back, and the URL unchanged while
              the user clicked through eight more fake screens. Four links the backend itself sent
              landed here; those are fixed, but a typed URL or one pasted from an old note cannot be
              guarded, which is why this had to stop being a page at all.
              App.jsx and screens/ stay in the repo — they are simply no longer routed. TweaksPanel
              went with them, and only ever appeared here, i.e. nowhere reachable in normal use. */}
          <Route path="/*" element={<NotFoundPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </React.Suspense>
        </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
